import React, { useState, useEffect } from 'react';
import { PurchaseHistory } from 'ai-api/types/purchase';
import PurchaseService from '../../Data/PurchaseService';
import {
    PurchaseHistoryList,
    PurchaseDetailsModal
} from '../../Components/Purchase';
import './index.scss';
import './HistoryFilters.scss';
import { useSession } from '@/Recoil/Session/Session';

const PurchaseHistoryPage: React.FC = () => {
    const { session } = useSession();
    const [purchases, setPurchases] = useState<PurchaseHistory[]>([]);
    const [filteredPurchases, setFilteredPurchases] = useState<PurchaseHistory[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedPurchase, setSelectedPurchase] = useState<PurchaseHistory | null>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);

    // Fetch purchase history
    useEffect(() => {
        const fetchPurchaseHistory = async () => {
            setLoading(true);
            setError(null);

            try {
                if (!session.email) {
                    throw new Error('You must be logged in to view your purchase history');
                }
                const response = await PurchaseService.getPurchaseHistory('product');

                if (response.code === 200 && response.data) {
                    setPurchases(response.data);
                    setFilteredPurchases(response.data);
                } else {
                    setError('Failed to load purchase history');
                }
            } catch (err) {
                console.error('Error fetching purchase history:', err);
                setError((err as Error).message || 'An error occurred while fetching purchase history');
            } finally {
                setLoading(false);
            }
        };

        fetchPurchaseHistory();
    }, [session]);

    // Filter purchases when search query changes
    useEffect(() => {
        let filtered = [...purchases];

        // Filter by search query
        if (searchQuery.trim() !== '') {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(purchase =>
                purchase.productName.toLowerCase().includes(query)
            );
        }

        // Sort by created_at (most recent first)
        filtered.sort((a, b) => b.created_at - a.created_at);

        setFilteredPurchases(filtered);
    }, [purchases, searchQuery]);

    const handleSearch = (query: string) => {
        setSearchQuery(query);
    };

    const handleViewDetails = (purchase: PurchaseHistory) => {
        setSelectedPurchase(purchase);
        setShowDetailsModal(true);
    };

    const closeDetailsModal = () => {
        setShowDetailsModal(false);
    };

    return (
        <div className="purchase-history-page">
            <div className="page-content">
                <div className="purchase-history-filters">
                    <div className="filters-search">
                        <input
                            type="text"
                            placeholder="Search by teacher or product name..."
                            onChange={(e) => handleSearch(e.target.value)}
                            className="search-input"
                        />
                    </div>
                </div>

                <PurchaseHistoryList
                    purchases={filteredPurchases}
                    onViewDetails={handleViewDetails}
                    loading={loading}
                    error={error}
                />
            </div>

            <PurchaseDetailsModal
                isOpen={showDetailsModal}
                onClose={closeDetailsModal}
                purchase={selectedPurchase}
            />
        </div>
    );
};

export default PurchaseHistoryPage;